import { useMemo, useState } from 'react'
import { ArrowLeft, Paperclip, Loader2, Mail, AlertCircle, CheckCircle2 } from 'lucide-react'
import { useLinkEmail } from '@/hooks/use-link-email'
import { useAttachmentUpload } from '@/hooks/use-attachment-upload'
import type { Matter, LinkableEmail } from '@/types'

interface EmailPreviewScreenProps {
  matter: Matter
  email: LinkableEmail
  onBack: () => void
  onLinked: (linkedEmailId: string) => void
}

export function EmailPreviewScreen({ matter, email, onBack, onLinked }: EmailPreviewScreenProps) {
  const linkEmail = useLinkEmail()
  const { progress, uploadAttachments } = useAttachmentUpload()

  const visibleAttachments = useMemo(
    () => email.attachments.filter((a) => !a.isInline),
    [email.attachments]
  )

  // Default: every visible attachment selected for filing.
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(visibleAttachments.map((a) => a.id))
  )
  const [alwaysAutoFile, setAlwaysAutoFile] = useState(false)
  const [uploading, setUploading] = useState(false)

  const from = email.from
    ? `${email.from.name ?? email.from.email} <${email.from.email}>`
    : '—'
  const toList = email.to
    .slice(0, 3)
    .map((r) => r.name ?? r.email)
    .join(', ')
  const snippet = email.bodyText?.trim().slice(0, 200) ?? null

  function toggle(id: string) {
    setSelected((cur) => {
      const next = new Set(cur)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function handleLink() {
    try {
      const toUpload = visibleAttachments.filter((a) => selected.has(a.id))
      let matterDocumentIds: string[] = []

      if (toUpload.length > 0) {
        setUploading(true)
        const uploaded = await uploadAttachments(matter.id, toUpload)
        matterDocumentIds = uploaded.map((u) => u.documentId)
        setUploading(false)
      }

      const result = await linkEmail.mutateAsync({
        matterId: matter.id,
        email,
        ...(matterDocumentIds.length > 0 ? { matterDocumentIds } : {}),
        ...(alwaysAutoFile ? { alwaysAutoFile: true } : {}),
      })
      onLinked(result.id)
    } catch {
      setUploading(false)
      // Error state surfaced via linkEmail.error / progress entries
    }
  }

  function progressFor(id: string) {
    return progress.find((p) => p.attachmentId === id)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Matter header */}
      <div className="px-4 py-3 border-b border-secondary-100 shrink-0 bg-secondary-50">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 text-xs text-secondary-500 hover:text-secondary-700 transition-colors mb-1"
        >
          <ArrowLeft size={12} />
          Back to matters
        </button>
        <p className="text-xs text-secondary-500">Linking to</p>
        <p className="text-sm font-semibold text-secondary-900 truncate">{matter.name}</p>
        {matter.reference && (
          <p className="text-xs text-secondary-400">{matter.reference}</p>
        )}
      </div>

      {/* Email preview */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-full bg-brand-gradient flex items-center justify-center shrink-0">
            <Mail size={14} className="text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-secondary-900 truncate">
              {email.subject ?? '(no subject)'}
            </p>
            <p className="text-xs text-secondary-500 mt-0.5 truncate">From: {from}</p>
            {email.to.length > 0 && (
              <p className="text-xs text-secondary-400 truncate">
                To: {toList}{email.to.length > 3 && ` +${email.to.length - 3} more`}
              </p>
            )}
            {email.dateTimeCreated && (
              <p className="text-xs text-secondary-400">
                {new Date(email.dateTimeCreated).toLocaleString()}
              </p>
            )}
          </div>
        </div>

        {snippet && (
          <div className="rounded-lg bg-secondary-50 p-3">
            <p className="text-xs text-secondary-600 whitespace-pre-wrap line-clamp-6">{snippet}</p>
          </div>
        )}

        {visibleAttachments.length > 0 && (
          <div className="rounded-lg border border-secondary-200 divide-y divide-secondary-100">
            <div className="px-3 py-2 text-xs font-medium text-secondary-700 bg-secondary-50">
              Attachments — file to /Correspondence/Emails/
            </div>
            {visibleAttachments.map((a) => {
              const isChecked = selected.has(a.id)
              const p = progressFor(a.id)
              return (
                <label
                  key={a.id}
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-secondary-50/60"
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={uploading || linkEmail.isPending}
                    onChange={() => toggle(a.id)}
                    className="h-3.5 w-3.5 shrink-0"
                  />
                  <Paperclip size={11} className="text-secondary-500 shrink-0" />
                  <span className="text-xs truncate flex-1">{a.name}</span>
                  <span className="text-xs text-secondary-400 shrink-0">{formatBytes(a.size)}</span>
                  {p?.state === 'done' && (
                    <CheckCircle2 size={12} className="text-emerald-600 shrink-0" />
                  )}
                  {p?.state === 'error' && (
                    <AlertCircle
                      size={12}
                      className="text-red-600 shrink-0"
                      aria-label={p.error}
                    />
                  )}
                  {p && p.state !== 'done' && p.state !== 'error' && (
                    <Loader2 size={12} className="text-secondary-500 animate-spin shrink-0" />
                  )}
                </label>
              )
            })}
            <label className="flex items-center gap-2 px-3 py-2 text-xs text-secondary-700 hover:bg-secondary-50/60 cursor-pointer">
              <input
                type="checkbox"
                checked={alwaysAutoFile}
                disabled={uploading || linkEmail.isPending}
                onChange={(e) => setAlwaysAutoFile(e.target.checked)}
                className="h-3.5 w-3.5 shrink-0"
              />
              <span>Always auto-file attachments for this matter</span>
            </label>
          </div>
        )}

        {linkEmail.isError && (
          <div className="rounded-lg bg-error-DEFAULT/5 border border-error-DEFAULT/20 p-3">
            <p className="text-xs text-error-DEFAULT">
              {(linkEmail.error as { message?: string }).message ?? 'Failed to link email. Please try again.'}
            </p>
          </div>
        )}
      </div>

      {/* Action */}
      <div className="p-4 border-t border-secondary-100 shrink-0">
        <button
          onClick={() => void handleLink()}
          disabled={linkEmail.isPending || uploading}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium text-white bg-brand-gradient hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          {uploading ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Uploading attachments…
            </>
          ) : linkEmail.isPending ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Linking…
            </>
          ) : selected.size > 0 ? (
            `Link & file ${selected.size} attachment${selected.size === 1 ? '' : 's'}`
          ) : (
            'Link to matter'
          )}
        </button>
      </div>
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
